import axios, { CancelToken } from 'axios';
import fileDownload from 'js-file-download';
import * as ls from 'local-storage';

import { STORAGE_TOKEN } from 'Common/Constants';
import { baseUrl } from './Connection';
import SecurityApi from 'Common/Security/api/SecurityApi';
import { FileDownloadTypes } from 'Common/Constants/Enumeration/CommonEnum';
import { base64ToBlob } from 'Common/IEHelper';
import { TokenManager } from 'Common/Security/TokenManager';

import { generateFileProgressEvent } from '../common/FileProgress';
import { RegisterUnauthorizedInteceptors } from './Interceptors';
import { ImportStudentsApi } from '../pages/importstudents/api/ImportStudentsApi';
import ImportSiteApi from '../pages/importsite/api/ImportSiteApi';
import LicenseApi from '../pages/license/api/LicenseApi';
import LoginApi from '../pages/login/api/LoginApi';

const parseResponseObject = (object) => {
  const { response } = object;

  if (!response) {
    const { data = {} } = object;
    const { Data, Errors = [] } = data;

    return [Data, Errors];
  }

  const { args } = object;
  const { data = {} } = response;
  const { Data, Errors = [] } = data;

  return [Data, Errors, args];
};

RegisterUnauthorizedInteceptors();

export class Api {
  config = {
    headers: {
      Accept: 'application/json',
      'content-Type': 'application/json; charset=utf-8',
    },
  };

  token = null;
  source = null;

  constructor(token) {
    /**
     * TODO: refactor API to use a created axios instance instead of using global axios instance
     */
    this.baseUrl = baseUrl;
    this.loginApi = new LoginApi(this.baseUrl, this.config);
    if (token) {
      this.setToken(token);
    }
  }

  setToken = (token) => {
    if (token !== null && token !== undefined) {
      this.config.headers.Authorization = `Bearer ${token}`;
      this.token = token;
    }

    this.importStudentsApi = new ImportStudentsApi(this.baseUrl, this.config);
    this.importSiteApi = new ImportSiteApi(this.baseUrl, this.config);
    this.licenseApi = new LicenseApi(this.baseUrl, this.config);
    this.securityApi = new SecurityApi(this.baseUrl, this.config);
  };

  setSource = (source) => {
    this.source = source;

    this.config = {
      ...this.config,
      cancelToken: source.token,
    };
  };

  createCancelableApi = () => {
    const source = CancelToken.source();
    const api = new Api(this.token);

    api.setSource(source);
    return {
      Api: api,
      source,
    };
  };

  getGeneric(resourceName, params = {}) {
    const url = `${this.baseUrl}/api/${resourceName}`;

    return new Promise((resolve, reject) => {
      axios
        .get(url, { ...this.config, params })
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getGenericWithData(resourceName, ...args) {
    return new Promise((resolve) => {
      this.getGeneric(resourceName).then((response) => {
        resolve({
          response,
          args: args,
        });
      });
    });
  }

  postGenericDelete(resourceName, id) {
    const url = `${this.baseUrl}/api/${resourceName}/delete/${id}/`;

    return new Promise((resolve, reject) => {
      axios
        .post(url, null, this.config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  postGenericDeleteQueryString(resourceName, id) {
    const url = `${
      this.baseUrl
    }/api/${resourceName}/delete?id=${encodeURIComponent(id)}`;

    return new Promise((resolve, reject) => {
      axios
        .post(url, null, this.config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  getDownloadFile(resourceName, fileName, fileType = FileDownloadTypes.CSV) {
    const url = `${this.baseUrl}/api/${resourceName}/`;

    const alteredConfig = {
      ...this.config,
      headers: {
        ...this.config.headers,
        Accept: fileType,
      },
    };

    return new Promise((resolve, reject) => {
      axios
        .get(url, alteredConfig)
        .then((response) => {
          if (response.data.length > 0) {
            fileDownload(response.data, fileName, fileType);
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  postDownloadFileAsIs(
    resourceName,
    fileName,
    data,
    fileType = FileDownloadTypes.CSV
  ) {
    const url = `${this.baseUrl}/api/${resourceName}/`;

    const alteredConfig = {
      ...this.config,
      headers: {
        ...this.config.headers,
        Accept: fileType,
      },
    };
    return new Promise((resolve, reject) => {
      axios
        .post(url, data, alteredConfig)
        .then((response) => {
          if (response.data.length > 0) {
            fileDownload(response.data, fileName, fileType);
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  postDownloadFile(
    resourceName,
    fileName,
    data,
    fileType = FileDownloadTypes.CSV
  ) {
    const url = `${this.baseUrl}/api/${resourceName}/`;

    const alteredConfig = {
      ...this.config,
      headers: {
        ...this.config.headers,
        Accept: fileType,
      },
    };
    return new Promise((resolve, reject) => {
      axios
        .post(url, data, alteredConfig)
        .then((response) => {
          if (response.data.Data.length > 0) {
            fileDownload(
              base64ToBlob(response.data.Data, fileType),
              fileName,
              fileType
            );
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  postGenericInsertUpdate(resourceName, data) {
    const url = `${this.baseUrl}/api/${resourceName}/`;

    return new Promise((resolve, reject) => {
      axios
        .post(url, data, this.config)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  postGenericFile(resourceName, data, dispatch, displayFileProgress = true) {
    const url = `${this.baseUrl}/api/${resourceName}/`;

    const alteredConfig = {
      ...this.config,
      headers: {
        ...this.config.headers,
        'content-type': 'multipart/form-data',
      },
    };

    if (displayFileProgress && dispatch)
      alteredConfig.onUploadProgress = generateFileProgressEvent(dispatch);

    return new Promise((resolve, reject) => {
      axios
        .post(url, data, alteredConfig)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  parseResponse(response) {
    return new Promise((resolve, reject) => {
      const parsedResults = parseResponseObject(response);
      const [data, errors, args] = parsedResults;

      if (errors.length) reject([errors, data, args]);
      else resolve(parsedResults);
    });
  }
}

const MPWApi = new Api();

const token = new TokenManager(
  () => ls.get(STORAGE_TOKEN) || {},
  (newToken) => ls.set(STORAGE_TOKEN, newToken),
  (refreshToken) => MPWApi.securityApi.postRefreshToken(refreshToken)
);

// Register a listener for when the token changes.
token.addOnChangeListener((token) => {
  MPWApi.setToken(token.accessToken);
}, true);

export { token, MPWApi };
export default MPWApi;



// WEBPACK FOOTER //
// ./src/api/MPWApi.js